import { config, IS_DEV } from "@/config/env";
import { getCurrentUser } from "@/global";
import {
  createLocalInterviewBackendMemoryStorage,
  LocalInterviewBackend,
  type AuthConfigGetter,
  type ManagerOptions,
} from "@imminently/interview-sdk";
import { useMemo } from "react";
import { observeTenancyApiResponseData, requireActiveTenancy } from "@/features/tenancy";

const tenancyAwareOverrides = () => ({
  // Response transforms run before Axios rejects an invalid status, including
  // for Interview SDK file operations that catch their own request errors.
  transformResponse: [observeTenancyApiResponseData],
});

// simply map our auth to target struct
export const getAuth: AuthConfigGetter = () => {
  // need to manually build, as azureAuthBearerHeader is async
  const user = getCurrentUser();

  if (user) {
    return {
      token: `Bearer ${user.id_token}`,
      tenancy: requireActiveTenancy(),
    };
  }

  return { token: "", tenancy: "" };
};

export const createLocalInterviewPreviewBackend = () =>
  new LocalInterviewBackend({
    host: config.BACKEND_URL,
    storage: createLocalInterviewBackendMemoryStorage(),
    auth: getAuth,
    overrides: tenancyAwareOverrides(),
  });

export const getManagerOptions = (options: Partial<ManagerOptions> = {}) => {
  const sessionBackend = {
    host: config.BACKEND_URL,
    path: "decisionapi/session",
    auth: getAuth,
    overrides: tenancyAwareOverrides(),
  };

  return {
    debug: IS_DEV,
    preCacheClient: true,
    backend: sessionBackend,
    fileManager: {
      host: config.BACKEND_URL,
      // TODO confirm path
      filePath: ["decisionapi", "custom", "file"],
      auth: getAuth,
      overrides: tenancyAwareOverrides(),
    },
    ...options,
  } as ManagerOptions;
};

/**
 * A basic hook with interview sdk configuration
 *
 * @param init Callback to initialize interview session, use this to create or load sessions
 * @param options Additional options to customize the interview manager
 */
export const useBasicInterview = (init: ManagerOptions["init"], options: Partial<ManagerOptions> = {}) => {
  // DEV NOTE: we are still using the old config, so we must reference all caps raw value names
  return useMemo(
    () => getManagerOptions({ init, ...options }),
    [init, options],
  );
};
